/* eslint-disable no-console */
import {InfoCircleOutlined} from '@ant-design/icons';
import {Col, Form, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useContext, useEffect, useMemo, useState} from 'react';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {API_ENDPOINT_JSON} from '../../config';
import {CommonErrors, TF} from '../../constants/Common';
import FilteredUnit from '../../constants/FilteredUnit';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {sortYearOptions} from '../../pages/orgManager/Utils/InfraStructureProfile/constant/sorYear';
import NumberInput from '../../shared/NumberInput';
import yearFilterArray from '../../shared/YearFilterArray';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import RegEx from '../../utils/FormUtils/RegEx';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';

import {
  documentUrlKeyLabel,
  facilityKeyLabel,
  yearKeyLabel
} from './Constants/IP/commonKeyLabel';
import {FuelKey, FuelLabel, FuelTooltip} from './Constants/IP/FuelKeyLabel';
import {unitType, unitTypes} from './interface/unitChange';
import AttachedDocumentUpload from './Utils/AttachedDocumentUpload';
import {getUnits} from './Utils/changeUnit';
import {filterSelectOptions} from './Utils/commonFunctions';

const FuelForm: React.FC<{
  currentStep?: number;
  defaultDisplayStep?: boolean;
}> = ({currentStep, defaultDisplayStep = false}) => {
  const isCompanyUserOrAdmin =
    hasUserRole(UserRole.Company_Admin) || hasUserRole(UserRole.Company_user);
  const {getFieldValue, setFields} = useFormInstance();
  const deviceType = useJSONStore.getState().deviceType;
  const fuelUsed = useJSONStore.getState().deviceFuel;
  const [fuelState, setFuelState] = useState<unitType>(unitTypes.WEIGHT);
  const {efdbData, facilityId, selectedYear, selectedCountry} = useContext(
    SelectedCountryContext
  );

  const [formSelectedCountry, setFormSelectedCountry] = useState<string>('');

  const [facilityOpt, setFacilityOpt] = useState<any[]>([]);
  const [facilityEfdb, setFacilityEfdb] = useState<any>();

  const userDetails = useUserStore.getState().userDetails;
  const facilityDetails = useCompanyStore.getState().facilityDetails ?? [];

  useEffect(() => {
    setFacilityOpt(
      facilityDetails?.[
        getFieldValue(yearKeyLabel.key) ?? selectedYear ?? ''
      ] ?? []
    );

    setFacilityEfdb(
      efdbData?.[getFieldValue(yearKeyLabel.key) ?? selectedYear ?? '']?.find(
        (i: any) => {
          return (
            i.facilityId ===
            (getFieldValue(facilityKeyLabel.key) ?? facilityId ?? '')
          );
        }
      )
    );
  }, []);

  useEffect(() => {
    setFuelState(
      fuelUsed.find((item) => {
        return (
          item?.value === getFieldValue(FuelKey.FUEL_USED) &&
          item?.dbName === facilityEfdb?.scope1
        );
      })?.state ?? ''
    );
  }, [facilityEfdb]);

  useEffect(() => {
    setFormSelectedCountry(
      facilityOpt?.find((item: any) => {
        return (
          item?._id ===
          (getFieldValue(facilityKeyLabel.key) ?? facilityId ?? '')
        );
      })?.country
    );
  }, [facilityOpt]);

  const monYear: MYConfig[] = useMemo(() => {
    return [
      {
        mainTitle: {
          key: FuelKey.QUANTITY_TF,
          title: FuelLabel.QUANTITY_TF,
          tooltip: FuelTooltip.QUANTITY
        },
        unit: getUnits(
          fuelState,
          FilteredUnit(formSelectedCountry ?? selectedCountry)
        )
      },
      {
        mainTitle: {
          key: FuelKey.COST_TF,
          title: FuelLabel.COST_TF,
          tooltip: FuelTooltip.FUEL_COST
        },
        unit: FilteredUnit(formSelectedCountry ?? selectedCountry).currency
      }
    ];
  }, [formSelectedCountry, selectedCountry, fuelState]);

  const [yearOptions, setYearOptions] = useState<any[]>([]);

  useEffect(() => {
    setYearOptions(
      yearFilterArray(
        facilityDetails,
        !isCompanyUserOrAdmin ? userDetails?.facility : undefined
      )
    );
  }, []);

  return (
    <>
      {(currentStep === 0 || defaultDisplayStep) && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              initialValue={selectedYear}
              hidden={defaultDisplayStep}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={sortYearOptions(yearOptions)}
                suffixIcon={<YearCalender />}
                disabled={getFieldValue('_id') !== undefined}
                onChange={(e) => {
                  setFacilityOpt(facilityDetails?.[e] ?? []);
                  if (getFieldValue(facilityKeyLabel.key)) {
                    setFields([
                      {
                        name: facilityKeyLabel.key,
                        value: undefined,
                        errors: ['Please select facility again']
                      }
                    ]);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {isCompanyUserOrAdmin && (
            <>
              <Col className="gutter-row">
                <Form.Item
                  name={facilityKeyLabel.key}
                  label={facilityKeyLabel.label}
                  labelCol={{span: 24}}
                  hidden={defaultDisplayStep}
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory(facilityKeyLabel.label)
                    }
                  ]}
                  initialValue={facilityId}
                >
                  <Select
                    placeholder={`Choose ${facilityKeyLabel.label}`}
                    options={
                      facilityOpt?.map((item: any) => {
                        return {label: item.name, value: item._id};
                      }) ?? []
                    }
                    disabled={getFieldValue('_id') !== undefined}
                    onChange={(e) => {
                      setFacilityEfdb(() => {
                        return efdbData?.[
                          getFieldValue(yearKeyLabel.key)
                        ]?.find((i: any) => {
                          return i?.facilityId === e;
                        });
                      });
                      if (getFieldValue(FuelKey.FUEL_USED)) {
                        setFields([
                          {
                            name: FuelKey.FUEL_USED,
                            errors: [CommonErrors.selectAgain],
                            value: undefined
                          }
                        ]);
                      }
                      setFormSelectedCountry(
                        facilityDetails?.[getFieldValue(yearKeyLabel.key)].find(
                          (item: any) => {
                            return item._id === e;
                          }
                        )?.country
                      );
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </>
      )}
      {(currentStep === 1 || defaultDisplayStep) && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={FuelKey.DEVICE_TYPE}
              // rules={[
              //   {
              //     required: true,
              //     message: CommonErrors.mandatory(FuelLabel.DEVICE_TYPE)
              //   }
              // ]}
              label={FuelLabel.DEVICE_TYPE}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Select
                showSearch
                filterOption={filterSelectOptions}
                className="select"
                placeholder={`Choose ${FuelLabel.DEVICE_TYPE}`}
                allowClear
                options={deviceType}
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={6}> */}
          <Col className="gutter-row">
            <Form.Item
              name={FuelKey.NO_UNITS}
              label={FuelLabel.NO_UNITS}
              rules={[
                {
                  pattern: RegEx.nonDecimal,
                  message: CommonErrors.nonDecimalNonNegative
                }
              ]}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <NumberInput
                placeholder={`Enter ${FuelLabel.NO_UNITS}`}
                isFormatted={true}
              />
            </Form.Item>
          </Col>
          {/* <Col className="gutter-row" span={6}> */}
          <Col className="gutter-row">
            <Form.Item
              dependencies={[FuelKey.FACILITY, FuelKey.YEAR]}
              noStyle
            >
              {() => {
                return (
                  <Form.Item
                    name={FuelKey.FUEL_USED}
                    label={FuelLabel.FUEL_USED}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(FuelLabel.FUEL_USED)
                      }
                    ]}
                    labelCol={{span: 24}}
                    validateFirst
                    hasFeedback
                    tooltip={{
                      icon: <InfoCircleOutlined />,
                      title: FuelTooltip.FUEL_USED
                    }}
                  >
                    <Select
                      showSearch
                      filterOption={filterSelectOptions}
                      className="select"
                      placeholder={`Choose ${FuelLabel.FUEL_USED}`}
                      options={fuelUsed.filter(
                        (item) => item?.dbName === facilityEfdb?.scope1
                      )}
                      onChange={(e, option) => {
                        setFuelState(option.state);
                      }}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        </>
      )}
      {(currentStep === 2 || defaultDisplayStep) && (
        <>
          {!defaultDisplayStep && (
            <Col className="gutter-row">
              <Form.Item
                name={documentUrlKeyLabel.key}
                label={documentUrlKeyLabel.label}
                labelCol={{span: 24}}
              >
                <AttachedDocumentUpload
                  uploadEndpoint={
                    API_ENDPOINT_JSON.machineUploadAttachedDocument
                  }
                />
              </Form.Item>
            </Col>
          )}
          <MonthlyYearlyDrawerForm
            config={monYear}
            data={getFieldValue(TF) as MYData[]}
          />
        </>
      )}
    </>
  );
};

export default FuelForm;
